/**
 * Performance Tab
 *
 * Detailed performance analysis with charts and distribution.
 */

import { useMemo } from "react";
import { formatCurrency, cn } from "@/lib/utils";
import { useTradingAnalysis } from "../trading-analysis-context";
import { StatCard } from "./shared/stat-card";
import { StatGrid } from "./shared/stat-grid";
import { AnalysisCard } from "./shared/analysis-card";
import * as TooltipPrimitive from "@radix-ui/react-tooltip";
import {
  AreaChart,
  Area,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  ReferenceLine,
  Cell,
  LabelList,
} from "recharts";

/** Stat card with dashed underline tooltip on label */
function TooltipStatCard({
  label,
  value,
  variant = "default",
  tooltip,
}: {
  label: string;
  value: string | number;
  variant?: "default" | "positive" | "negative" | "neutral";
  tooltip: string;
}) {
  const valueColorClass = {
    default: "text-foreground",
    positive: "text-bid-foreground",
    negative: "text-ask-foreground",
    neutral: "text-muted-foreground",
  }[variant];

  return (
    <div className="rounded-lg bg-muted/30 p-3 transition-colors hover:bg-muted/40">
      <div className="flex items-center gap-1.5 mb-1">
        <TooltipPrimitive.Provider delayDuration={250}>
          <TooltipPrimitive.Root>
            <TooltipPrimitive.Trigger asChild>
              <span className="text-xs text-muted-foreground truncate border-b border-dashed border-muted-foreground/50 cursor-default select-none">
                {label}
              </span>
            </TooltipPrimitive.Trigger>
            <TooltipPrimitive.Portal>
              <TooltipPrimitive.Content
                side="top"
                sideOffset={5}
                className={cn(
                  "z-50 max-w-[280px] rounded-md px-3 py-2",
                  "bg-card/95 text-foreground/90 backdrop-blur-sm",
                  "border border-border dark:border-muted-foreground/20 shadow-lg",
                  "animate-in fade-in-0 zoom-in-95",
                  "data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95",
                  "data-[side=bottom]:slide-in-from-top-2 data-[side=top]:slide-in-from-bottom-2"
                )}
              >
                <p className="text-xs leading-relaxed">{tooltip}</p>
              </TooltipPrimitive.Content>
            </TooltipPrimitive.Portal>
          </TooltipPrimitive.Root>
        </TooltipPrimitive.Provider>
      </div>
      <div className={cn("text-lg font-bold leading-tight", valueColorClass)}>
        {value}
      </div>
    </div>
  );
}

export function PerformanceTab() {
  const { stats, filteredTrades } = useTradingAnalysis();

  const isEmpty = !filteredTrades.length;

  // Fixed logarithmic buckets - consistent for all accounts (always define for empty state)
  const emptyBuckets: Array<{ label: string; shortLabel: string; min: number; max: number; count: number; isLoss: boolean; isNeutral: boolean }> = [
    { label: "< -$10,000", shortLabel: "< -$10k", min: -Infinity, max: -10000, count: 0, isLoss: true, isNeutral: false },
    { label: "-$10,000 to -$1,000", shortLabel: "-$10k to -$1k", min: -10000, max: -1000, count: 0, isLoss: true, isNeutral: false },
    { label: "-$1,000 to -$100", shortLabel: "-$1k to -$100", min: -1000, max: -100, count: 0, isLoss: true, isNeutral: false },
    { label: "-$100 to -$10", shortLabel: "-$100 to -$10", min: -100, max: -10, count: 0, isLoss: true, isNeutral: false },
    { label: "-$10 to $0", shortLabel: "-$10 to $0", min: -10, max: 0, count: 0, isLoss: true, isNeutral: false },
    { label: "$0 to $10", shortLabel: "$0 to $10", min: 0, max: 10, count: 0, isLoss: false, isNeutral: true },
    { label: "$10 to $100", shortLabel: "$10 to $100", min: 10, max: 100, count: 0, isLoss: false, isNeutral: false },
    { label: "$100 to $1,000", shortLabel: "$100 to $1k", min: 100, max: 1000, count: 0, isLoss: false, isNeutral: false },
    { label: "$1,000 to $10,000", shortLabel: "$1k to $10k", min: 1000, max: 10000, count: 0, isLoss: false, isNeutral: false },
    { label: "> $10,000", shortLabel: "> $10k", min: 10000, max: Infinity, count: 0, isLoss: false, isNeutral: false },
  ];

  // Calculate P&L distribution with fixed logarithmic buckets
  const distribution = useMemo(() => {
    if (!filteredTrades.length) return emptyBuckets;

    // Fixed logarithmic buckets - consistent for all accounts
    const buckets: Array<{ label: string; shortLabel: string; min: number; max: number; count: number; isLoss: boolean; isNeutral: boolean }> = [
      { label: "< -$10,000", shortLabel: "< -$10k", min: -Infinity, max: -10000, count: 0, isLoss: true, isNeutral: false },
      { label: "-$10,000 to -$1,000", shortLabel: "-$10k to -$1k", min: -10000, max: -1000, count: 0, isLoss: true, isNeutral: false },
      { label: "-$1,000 to -$100", shortLabel: "-$1k to -$100", min: -1000, max: -100, count: 0, isLoss: true, isNeutral: false },
      { label: "-$100 to -$10", shortLabel: "-$100 to -$10", min: -100, max: -10, count: 0, isLoss: true, isNeutral: false },
      { label: "-$10 to $0", shortLabel: "-$10 to $0", min: -10, max: 0, count: 0, isLoss: true, isNeutral: false },
      { label: "$0 to $10", shortLabel: "$0 to $10", min: 0, max: 10, count: 0, isLoss: false, isNeutral: true },
      { label: "$10 to $100", shortLabel: "$10 to $100", min: 10, max: 100, count: 0, isLoss: false, isNeutral: false },
      { label: "$100 to $1,000", shortLabel: "$100 to $1k", min: 100, max: 1000, count: 0, isLoss: false, isNeutral: false },
      { label: "$1,000 to $10,000", shortLabel: "$1k to $10k", min: 1000, max: 10000, count: 0, isLoss: false, isNeutral: false },
      { label: "> $10,000", shortLabel: "> $10k", min: 10000, max: Infinity, count: 0, isLoss: false, isNeutral: false },
    ];

    // Count trades in each bucket
    for (const trade of filteredTrades) {
      const pnl = trade.netPnl;
      for (const bucket of buckets) {
        if (pnl >= bucket.min && pnl < bucket.max) {
          bucket.count++;
          break;
        }
      }
    }

    return buckets;
  }, [filteredTrades]);

  // Calculate cumulative P&L for equity curve
  const equityCurve = useMemo(() => {
    if (!filteredTrades.length) return [];

    const sortedTrades = [...filteredTrades].sort(
      (a, b) => a.exitTime.getTime() - b.exitTime.getTime()
    );

    let cumulative = 0;
    let peak = 0;
    const points: Array<{
      trade: number;
      pnl: number;
      cumulative: number;
      drawdown: number;
    }> = [];

    sortedTrades.forEach((trade, i) => {
      cumulative += trade.netPnl;
      if (cumulative > peak) peak = cumulative;
      const drawdown = peak - cumulative;

      points.push({
        trade: i + 1,
        pnl: trade.netPnl,
        cumulative,
        drawdown,
      });
    });

    return points;
  }, [filteredTrades]);

  return (
    <div className="space-y-4">
      {/* Key Performance Metrics */}
      <AnalysisCard title="Performance Metrics" subtitle="Your trading results for the selected period">
        <StatGrid columns={4} gap="sm">
          <StatCard
            label="Total Net P&L"
            value={isEmpty ? "$0.00" : `${stats.totalNetPnl >= 0 ? "+" : "-"}$${formatCurrency(Math.abs(stats.totalNetPnl), 2)}`}
            variant={isEmpty ? "default" : (stats.totalNetPnl >= 0 ? "positive" : "negative")}
            tooltip="Total profit minus total loss, after all fees"
          />
          <StatCard
            label="Win Rate"
            value={isEmpty ? "0.0%" : `${stats.winRate.toFixed(1)}%`}
            variant="default"
            tooltip="% of trades closed in profit. Above 50% is good, but risk/reward matters more."
          />
          <StatCard
            label="Profit Factor"
            value={isEmpty ? "0.00" : (stats.profitFactor === Infinity ? "∞" : stats.profitFactor.toFixed(2))}
            variant="default"
            tooltip="Gross profit ÷ gross loss. Above 1.5 is good, above 2.0 is excellent."
          />
          <StatCard
            label="Expectancy"
            value={isEmpty ? "$0.00" : `${stats.expectancy >= 0 ? "+" : "-"}$${formatCurrency(Math.abs(stats.expectancy), 2)}`}
            variant={isEmpty ? "default" : (stats.expectancy >= 0 ? "positive" : "negative")}
            tooltip="Average expected profit per trade. A positive value means your strategy is profitable over time."
          />
        </StatGrid>
      </AnalysisCard>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Equity Curve - Area Chart */}
        <AnalysisCard title="Equity Curve" subtitle="Your account balance over time">
          <div className="space-y-3">
            {/* Mini stats */}
            <div className="grid grid-cols-3 gap-2 text-center">
              <div>
                <div className="text-[10px] text-muted-foreground">Start</div>
                <div className="text-sm font-semibold">$0</div>
              </div>
              <div>
                <div className="text-[10px] text-muted-foreground">Peak</div>
                <div className={cn("text-sm font-semibold", isEmpty ? "text-foreground" : "text-bid-foreground")}>
                  {isEmpty ? "$0" : `+$${formatCurrency(Math.max(...equityCurve.map((p) => p.cumulative), 0), 0)}`}
                </div>
              </div>
              <div>
                <div className="text-[10px] text-muted-foreground">Current</div>
                <div className={cn(
                  "text-sm font-semibold",
                  isEmpty ? "text-foreground" : (stats.totalNetPnl >= 0 ? "text-bid-foreground" : "text-ask-foreground")
                )}>
                  {isEmpty ? "$0" : `${stats.totalNetPnl >= 0 ? "+" : ""}$${formatCurrency(stats.totalNetPnl, 0)}`}
                </div>
              </div>
            </div>

            {/* Area Chart for Equity Curve - uses gradient fill from bottom */}
            <div className="h-28 relative">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart
                  data={isEmpty ? [{ trade: 0, cumulative: 0 }] : equityCurve}
                  margin={{ top: 5, right: 5, left: 5, bottom: 5 }}
                >
                  <defs>
                    <linearGradient id="equityGradientPositive" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="var(--color-bid-foreground)" stopOpacity={0.35} />
                      <stop offset="100%" stopColor="var(--color-bid-foreground)" stopOpacity={0.05} />
                    </linearGradient>
                    <linearGradient id="equityGradientNegative" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="var(--color-ask-foreground)" stopOpacity={0.1} />
                      <stop offset="100%" stopColor="var(--color-ask-foreground)" stopOpacity={0.35} />
                    </linearGradient>
                  </defs>
                  <ReferenceLine y={0} stroke="var(--border)" strokeDasharray="3 3" />
                  {!isEmpty && (
                    <RechartsTooltip
                      cursor={false}
                      content={({ active, payload }) => {
                        if (!active || !payload?.length) return null;
                        const data = payload[0].payload;
                        return (
                          <div className="bg-card border border-border rounded-lg p-2 shadow-lg text-xs">
                            <p className="text-muted-foreground mb-1">Trade #{data.trade}</p>
                            <p className={cn(
                              "font-semibold",
                              data.cumulative >= 0 ? "text-bid-foreground" : "text-ask-foreground"
                            )}>
                              P&L: {data.cumulative >= 0 ? "+" : ""}${formatCurrency(data.cumulative, 2)}
                            </p>
                          </div>
                        );
                      }}
                    />
                  )}
                  {!isEmpty && (
                    <Area
                      type="monotone"
                      dataKey="cumulative"
                      stroke={stats.totalNetPnl >= 0 ? "var(--color-bid-foreground)" : "var(--color-ask-foreground)"}
                      strokeWidth={2}
                      fill={stats.totalNetPnl >= 0 ? "url(#equityGradientPositive)" : "url(#equityGradientNegative)"}
                    />
                  )}
                </AreaChart>
              </ResponsiveContainer>
              {isEmpty && (
                <div className="absolute inset-0 flex items-center justify-center">
                  <p className="text-xs text-muted-foreground">Your equity curve will appear here</p>
                </div>
              )}
            </div>
            <div className="text-[10px] text-muted-foreground text-center">
              A smooth upward curve indicates consistent profitability
            </div>
          </div>
        </AnalysisCard>

        {/* Drawdown - Underwater Area Chart */}
        <AnalysisCard title="Drawdown Analysis" subtitle="Measuring your downside risk">
          <div className="space-y-3">
            <StatGrid columns={2} gap="sm">
              <StatCard
                label="Max Drawdown"
                value={isEmpty ? "$0" : `$${formatCurrency(stats.maxDrawdown, 0)}`}
                subValue={isEmpty ? "0.0%" : `${stats.maxDrawdownPercent.toFixed(1)}%`}
                variant={isEmpty ? "default" : "negative"}
                tooltip="Largest peak-to-trough decline. Keep below 20% for sustainable trading."
              />
              <StatCard
                label="Recovery Factor"
                value={isEmpty ? "0.00" : stats.recoveryFactor.toFixed(2)}
                variant="default"
                tooltip="Net profit ÷ max drawdown. Above 1.0 means you've recovered losses."
              />
            </StatGrid>

            {/* Drawdown Bar Chart - shows drawdown as bars dropping from zero */}
            <div className="h-20 relative">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={isEmpty ? [] : equityCurve.map(p => ({ ...p, drawdownPercent: stats.maxDrawdown > 0 ? (p.drawdown / stats.maxDrawdown) * 100 : 0 }))}
                  margin={{ top: 5, right: 5, left: 5, bottom: 5 }}
                >
                  {!isEmpty && (
                    <RechartsTooltip
                      cursor={false}
                      content={({ active, payload }) => {
                        if (!active || !payload?.length) return null;
                        const data = payload[0].payload;
                        return (
                          <div className="bg-card border border-border rounded-lg p-2 shadow-lg text-xs">
                            <p className="text-muted-foreground mb-1">Trade #{data.trade}</p>
                            <p className="text-ask-foreground font-semibold">
                              Drawdown: ${formatCurrency(data.drawdown, 2)}
                            </p>
                            <p className="text-muted-foreground text-[10px]">
                              {data.drawdownPercent.toFixed(1)}% of max
                            </p>
                          </div>
                        );
                      }}
                    />
                  )}
                  {!isEmpty && (
                    <Bar 
                      dataKey="drawdownPercent" 
                      fill="var(--color-ask-foreground)"
                      opacity={0.6}
                      radius={[2, 2, 0, 0]}
                    />
                  )}
                </BarChart>
              </ResponsiveContainer>
              {isEmpty && (
                <div className="absolute inset-0 flex items-center justify-center">
                  <p className="text-xs text-muted-foreground">Drawdown data will appear here</p>
                </div>
              )}
            </div>
            <div className="text-[10px] text-muted-foreground text-center">
              Smaller, shorter drawdowns indicate better risk management
            </div>
          </div>
        </AnalysisCard>
      </div>

      {/* P&L Distribution - Vertical Histogram */}
      <AnalysisCard title="P&L Distribution" subtitle="Net profit/loss per trade">
        <div className="h-56 relative">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={distribution}
              margin={{ top: 20, right: 10, left: 10, bottom: 50 }}
            >
              <XAxis
                dataKey="shortLabel"
                tick={{ fontSize: 9, fill: "var(--muted-foreground)" }}
                tickLine={false}
                axisLine={false}
                angle={-45}
                textAnchor="end"
                height={60}
                interval={0}
              />
              <YAxis
                tick={{ fontSize: 10, fill: "var(--muted-foreground)" }}
                tickLine={false}
                axisLine={false}
                width={30}
              />
              {!isEmpty && (
                <RechartsTooltip
                  cursor={false}
                  content={({ active, payload }) => {
                    if (!active || !payload?.length) return null;
                    const data = payload[0].payload;
                    return (
                      <div className="bg-card border border-border rounded-lg p-2 shadow-lg text-xs">
                        <p className="text-muted-foreground mb-1">{data.label}</p>
                        <p className="font-semibold">{data.count} trade{data.count !== 1 ? 's' : ''}</p>
                      </div>
                    );
                  }}
                />
              )}
              <Bar dataKey="count" radius={[4, 4, 0, 0]}>
                {!isEmpty && (
                  <LabelList 
                    dataKey="count" 
                    position="top" 
                    fontSize={10}
                    fill="var(--muted-foreground)"
                    formatter={(value) => (value && Number(value) > 0 ? value : '')}
                  />
                )}
                {distribution.map((entry, index) => {
                  // Use theme colors via CSS variables
                  let fill: string;
                  if (entry.isNeutral) {
                    fill = "var(--muted-foreground)";
                  } else if (entry.isLoss) {
                    fill = "var(--color-ask-foreground)";
                  } else {
                    fill = "var(--color-bid-foreground)";
                  }
                  // Apply opacity based on distance from zero (dimmer when empty)
                  const opacity = isEmpty ? 0.1 : (entry.isNeutral ? 0.5 : 0.75);
                  return <Cell key={`cell-${index}`} fill={fill} fillOpacity={opacity} />;
                })}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
          {isEmpty && (
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
              <p className="text-xs text-muted-foreground">P&L distribution will appear here</p>
            </div>
          )}
        </div>
        <div className="text-[10px] text-muted-foreground text-center mt-2 pt-2 border-t border-border/30">
          Ideal: More trades in green zones, fewer in red. Cut losers early, let winners run.
        </div>
      </AnalysisCard>

      {/* Risk Metrics */}
      <AnalysisCard title="Risk-Adjusted Returns" subtitle="How well you manage risk vs reward">
        <StatGrid columns={4} gap="sm">
          <TooltipStatCard
            label="Sharpe Ratio"
            value={isEmpty ? "0.00" : (stats.sharpeRatio !== null ? stats.sharpeRatio.toFixed(2) : "0.00")}
            variant="default"
            tooltip="Return per unit of risk. Above 1.0 is good, above 2.0 is excellent."
          />
          <TooltipStatCard
            label="Sortino Ratio"
            value={isEmpty ? "0.00" : (stats.sortinoRatio !== null ? stats.sortinoRatio.toFixed(2) : "0.00")}
            variant="default"
            tooltip="Like Sharpe but only penalizes downside volatility — better for traders."
          />
          <TooltipStatCard
            label="Risk/Reward"
            value={isEmpty ? "0.00:1" : `${stats.riskRewardRatio.toFixed(2)}:1`}
            variant="default"
            tooltip="Average win size ÷ average loss size. Above 1.0 means wins are bigger than losses."
          />
          <TooltipStatCard
            label="Avg Trade"
            value={isEmpty ? "$0.00" : `${stats.averageTrade >= 0 ? "+" : "-"}$${formatCurrency(Math.abs(stats.averageTrade), 2)}`}
            variant={isEmpty ? "default" : (stats.averageTrade >= 0 ? "positive" : "negative")}
            tooltip="Average profit or loss per trade after fees"
          />
        </StatGrid>
      </AnalysisCard>
    </div>
  );
}

